import React from 'react';
import type { HorseCare } from '../../../../types/horseProfile';

interface CareSectionProps {
  care: HorseCare;
}

const DEFAULT_CARE: HorseCare = {
  grooming: {
    frequency: '',
    specialInstructions: '',
    products: []
  },
  shoeing: {
    frequency: '',
    lastDate: null,
    nextDate: null,
    farrier: '',
    specialNeeds: ''
  },
  turnout: {
    schedule: '',
    preferences: '',
    restrictions: ''
  },
  equipment: []
};

export const CareSection: React.FC<CareSectionProps> = ({ care = DEFAULT_CARE }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Pflege</h4>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Häufigkeit:</strong> {care.grooming?.frequency}
          </p>
          {care.grooming?.specialInstructions && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              <strong>Besondere Anweisungen:</strong> {care.grooming.specialInstructions}
            </p>
          )}
          {care.grooming?.products && care.grooming.products.length > 0 && (
            <div className="mt-2">
              <strong className="text-sm text-gray-700 dark:text-gray-300">Pflegeprodukte:</strong>
              <ul className="mt-1 list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {care.grooming.products.map((product, index) => (
                  <li key={index}>{product}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Hufpflege</h4>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Häufigkeit:</strong> {care.shoeing?.frequency}
              </p>
              {care.shoeing?.farrier && (
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  <strong>Hufschmied:</strong> {care.shoeing.farrier}
                </p>
              )}
            </div>
            <div>
              {care.shoeing?.lastDate && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Letzter Termin:</strong>{' '}
                  {new Date(care.shoeing.lastDate).toLocaleDateString('de-DE')}
                </p>
              )}
              {care.shoeing?.nextDate && (
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  <strong>Nächster Termin:</strong>{' '}
                  {new Date(care.shoeing.nextDate).toLocaleDateString('de-DE')}
                </p>
              )}
            </div>
          </div>
          {care.shoeing?.specialNeeds && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              <strong>Besondere Anforderungen:</strong> {care.shoeing.specialNeeds}
            </p>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Koppelgang</h4>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Zeitplan:</strong> {care.turnout?.schedule}
          </p>
          {care.turnout?.preferences && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              <strong>Vorlieben:</strong> {care.turnout.preferences}
            </p>
          )}
          {care.turnout?.restrictions && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              <strong>Einschränkungen:</strong> {care.turnout.restrictions}
            </p>
          )}
        </div>
      </div>

      {care.equipment && care.equipment.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Ausrüstung</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {care.equipment.map((item, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</h5>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>Größe:</strong> {item.size}
                </p>
                {item.notes && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <strong>Anmerkungen:</strong> {item.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};